import React, { type ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Remove from '@assets/img/icons/e-remove.svg?react';
import { useTonConnectUI } from '@tonconnect/ui-react';
import cn from 'classnames';

import { useGetUserDecorationsQuery } from '@/api/queries/user';
import { ENV } from '@/common/app/env';
import { AppRoute } from '@/common/app/routes';
import { playSound, updateSoundSettings } from '@/common/lib/soundEffect';
import {
  getDecorationClass,
  getDecorationTitleKey,
  NO_DECORATION_TITLE,
} from '@/components/Leagues/PositionItem/decorations/registry';
import { toggleSettingMenu } from '@/redux/reducers/modalsReducer';
import { loggedOut } from '@/redux/reducers/userReducer';
import { useAppDispatch, useAppSelector } from '@/redux/store';

import css from './style.module.scss';

import NotificationBell from '/img/icons/notification-bell.svg';
import discord from '/img/icons/settings/discord.svg';
import languageIcon from '/img/icons/settings/language.svg';
import sounds from '/img/icons/settings/sounds.svg';
import telegram from '/img/icons/settings/telegram.svg';
import vibrationIcon from '/img/icons/settings/vibration.svg';
import x from '/img/icons/settings/x.svg';

type SettingsItemBase = {
  id: string;
  label: string;
  icon?: string | ReactNode;
  condition?: boolean;
  light?: boolean;
};

type SettingsItemLink = SettingsItemBase & {
  type: 'link';
  action: () => void;
  value?: string;
};

type SettingsItemToggle = SettingsItemBase & {
  type: 'toggle';
  action: () => void;
  state: boolean;
};

type SettingsItem = SettingsItemLink | SettingsItemToggle;

const SettingsSection: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const level = useAppSelector((state) => state.user.user?.level || 0);
  const unreadNotificationsCount = useAppSelector(
    (state) => state.user.unreadNotifications,
  );
  const { t } = useTranslation();
  const [language, setLanguage] = useState('English');
  const [vibration, setVibration] = useState(false);
  const [help, setHelp] = useState(false);
  const [soundEffect, setSoundEffect] = useState(false);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [tonConnectUI] = useTonConnectUI();
  const isTelegramMiniApp = !!Telegram?.WebApp?.initData;
  // Скины: пункт нужен только тем, у кого есть между чем переключаться.
  // Одного выигранного скина уже достаточно — вторым вариантом всегда идёт
  // «Стандартная плашка», её в items нет (см. ChangeSkin).
  const { data: decorations } = useGetUserDecorationsQuery();
  const ownedSkinsCount = decorations?.items.length ?? 0;
  const activeSkinId = decorations?.activeId ?? '';

  const closeModal = (): void => {
    dispatch(toggleSettingMenu());
  };

  const languagesList = [
    {
      code: 'en',
      language: 'English',
    },
    {
      code: 'ru',
      language: 'Русский',
    },
  ];

  useEffect(() => {
    const lan = localStorage.getItem('i18nextLng') || 'en';
    const obj = languagesList.find((el) => el.code === lan);
    const activeVibr = JSON.parse(
      localStorage.getItem('setHapticFeedback') || 'true',
    );
    setVibration(activeVibr);

    if (obj) setLanguage(obj.language);

    const activeHelp = JSON.parse(localStorage.getItem('help') || 'true');
    setHelp(activeHelp);
    const activeSound = JSON.parse(
      localStorage.getItem('setSoundEffect') || 'true',
    );
    setSoundEffect(activeSound);
  }, []);

  const disableVibration = (): void => {
    setVibration(!vibration);
    const string = JSON.stringify(!vibration);
    localStorage.setItem('setHapticFeedback', string);
  };
  const disableHelp = (): void => {
    setHelp((prevHelp) => {
      const newHelpState = !prevHelp;
      localStorage.setItem('help', JSON.stringify(newHelpState));
      window.dispatchEvent(new Event('storage'));

      return newHelpState;
    });
  };
  const disableSoundEffect = (): void => {
    const newState = !soundEffect;
    setSoundEffect(newState);
    updateSoundSettings(newState);
  };

  const openTgChannel = (): void => {
    Telegram.WebApp.openTelegramLink('https://t.me/starindustry_channel');
  };
  const openTgSupport = (): void => {
    Telegram.WebApp.openTelegramLink('https://t.me/StarIndustry_Support');
  };

  const openDiscord = (): void => {
    Telegram.WebApp.openLink('https://discord.gg/7dyYfSnGnA');
  };

  const openX = (): void => {
    Telegram.WebApp.openLink('https://x.com/starindustryx');
  };

  const handleLogout = async (): Promise<void> => {
    try {
      await tonConnectUI.disconnect();
    } catch {
      // wallet may not be connected
    }
    localStorage.removeItem('auth_token');
    dispatch(loggedOut());
    closeModal();
    navigate(AppRoute.LOGIN);
  };

  const settingsItems: SettingsItem[] = [
    {
      id: 'notifications',
      label: t('Notifications'),
      icon: (
        <div className="size-6 relative">
          <img
            src={NotificationBell}
            alt="Notifications"
            className="size-full"
          />
          {unreadNotificationsCount > 0 && (
            <div className="absolute right-0 top-0 bg-button-green-gradient rounded-full size-2.5 shadow-[0_0.5px_0_0_rgba(255,255,255,0.50)_inset]" />
          )}
        </div>
      ),
      type: 'link',
      value:
        unreadNotificationsCount > 0
          ? unreadNotificationsCount.toString()
          : undefined,
      action: (): void => {
        navigate('/notifications');
        closeModal();
      },
      condition: true,
      light: unreadNotificationsCount > 0,
    },
    {
      id: 'language',
      label: t('Language'),
      icon: languageIcon,
      type: 'link',
      action: (): void => {
        navigate('/settings/changeLanguage');
        closeModal();
      },
      value: language,
      condition: true,
    },
    {
      id: 'skin',
      label: t('Profile skin'),
      // Иконка — сам активный скин: отдельного ассета не нужно, и сразу
      // видно, что надето.
      icon: (
        <div
          className={cn(
            'size-6 rounded-md relative overflow-hidden',
            getDecorationClass(activeSkinId) ?? 'bg-white/10',
          )}
        />
      ),
      type: 'link',
      action: (): void => {
        navigate(`${AppRoute.SETTINGS}/changeSkin`);
        closeModal();
      },
      value: t(
        activeSkinId
          ? getDecorationTitleKey(activeSkinId)
          : NO_DECORATION_TITLE,
      ),
      condition: ownedSkinsCount > 0,
    },
    {
      id: 'vibration',
      label: t('Vibration'),
      icon: vibrationIcon,
      type: 'toggle',
      condition: true,
      state: vibration,
      action: disableVibration,
    },
    {
      id: 'tips',
      label: t('Tips'),
      icon: '/img/icons/helpCards/helpIcon.webp',
      type: 'toggle',
      condition: level < ENV.MIN_LEVEL_FOR_HELPICON,
      state: help,
      action: disableHelp,
    },
    {
      id: 'sounds',
      label: t('Sounds'),
      icon: sounds,
      type: 'toggle',
      condition: true,
      state: soundEffect,
      action: disableSoundEffect,
    },
    {
      id: 'telegram',
      label: t('Telegram Channel'),
      icon: telegram,
      type: 'link',
      action: openTgChannel,
      condition: true,
    },
    {
      id: 'discord',
      label: t('Discord Server'),
      icon: discord,
      type: 'link',
      action: openDiscord,
      condition: true,
    },
    {
      id: 'x',
      label: t('Our X'),
      icon: x,
      type: 'link',
      action: openX,
      condition: true,
    },
    {
      id: 'support',
      label: t('Support'),
      icon: telegram,
      type: 'link',
      action: openTgSupport,
      condition: true,
    },
    {
      id: 'terms',
      label: t('Terms of Use'),
      type: 'link',
      action: (): void => {
        navigate(AppRoute.TERMS);
        closeModal();
      },
      condition: true,
    },
    {
      id: 'privacy',
      label: t('Privacy Policy'),
      type: 'link',
      action: (): void => {
        navigate(AppRoute.PRIVACY);
        closeModal();
      },
      condition: true,
    },
    {
      id: 'logout',
      label: t('Log out'),
      type: 'link',
      action: handleLogout,
      condition: !isTelegramMiniApp,
    },
  ];

  return (
    <div className={css.settings}>
      <button
        className="p-1 rounded-full bg-white/10 backdrop-blur-lg"
        onClick={onClose}
      >
        <Remove className="size-6" />
      </button>
      <h3 className={css.settingsTitle}>{t('Settings')}</h3>
      <ul className={css.settingsList}>
        {settingsItems.map((item) => {
          if (!item.condition) {
            return null;
          }

          return (
            <li
              key={item.id}
              className={css.settingsListElement}
              onClick={() => {
                playSound(
                  `buttons/${item.type === 'link' ? 'menu' : 'switch'}`,
                );
                item.action();
              }}
            >
              <div>
                {typeof item.icon === 'string' ? (
                  item.icon.endsWith('.webp') ? (
                    <picture>
                      <source srcSet={item.icon} type="image/webp" />
                      <img
                        src={item.icon.replace('.webp', '.png')}
                        alt={item.label}
                        width={24}
                      />
                    </picture>
                  ) : (
                    <img
                      src={item.icon}
                      alt={item.label}
                      className="w-[24px] h-[24px]"
                    />
                  )
                ) : (
                  item.icon
                )}
                <span>{item.label}</span>
              </div>

              {item.type === 'link' && (
                <div>
                  {item.value && (
                    <span
                      className={cn(
                        item.id === 'notifications' && 'text-[#82E024]',
                      )}
                    >
                      {item.value}
                    </span>
                  )}
                  <img src="/img/icons/arrow-go.svg" alt="" />
                </div>
              )}

              {item.type === 'toggle' && (
                <div className={cn(css.sliderChange, item.state && css.active)}>
                  <div onClick={item.action}></div>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default SettingsSection;
